/**
 * Default suggested questions for the chat panel, in each UI locale. Three are picked
 * at random per session; the picks are kept as { zh, en } pairs and shown in the
 * current locale at render, so switching language re-labels the same questions.
 * The legacy text (UI_I18N_ENABLED off) shows zh, which is today's text.
 *
 * Each agent draws from its own pools (SUGGESTION_MIX_BY_AGENT): the Sales Agent's
 * Revenue / Pipeline questions are not the Marketing Agent's to answer, and the other
 * way round. Adding an agent's starters = one row in that table.
 */
import type { Locale } from '../i18n/resources';
import { EVAL_CHAT_AGENT_ID } from '../context/AgentContext';

export type SuggestedQuestion = Record<Locale, string>;

const BUSINESS_QUESTIONS: SuggestedQuestion[] = [
  { zh: '帮我看看我的 YTD Revenue 完成情况', en: 'Show me my YTD Revenue attainment' },
  { zh: '本周有哪些客户 Revenue 出现了明显下降？', en: 'Which customers saw a clear Revenue drop this week?' },
  { zh: '我管的区域 GenAI 收入占比是多少？', en: 'What share of my region\'s revenue comes from GenAI?' },
  { zh: 'CORE vs GenAI 最近 6 周的趋势怎么样？', en: 'How have CORE vs GenAI trended over the last 6 weeks?' },
  { zh: '本月 WoW Revenue 变化趋势如何？', en: 'How is Revenue trending WoW this month?' },
  { zh: '本月有哪些客户从零变成了有收入？', en: 'Which customers went from zero to revenue this month?' },
  { zh: '我的 Revenue 环比变化归因分析', en: 'Break down what drove my Revenue change month over month' },
  { zh: '哪些服务的 Usage 增长最快？', en: 'Which services are growing fastest in Usage?' },
  { zh: '本周有没有客户连续 3 天以上 Revenue 下滑？', en: 'Did any customer\'s Revenue fall for 3+ days in a row this week?' },
  { zh: '我管的客户 EDP/Credit 有没有即将到期的？', en: 'Do any of my customers have EDP/Credit about to expire?' },
  { zh: '帮我看看上周 vs 本周的 Revenue 对比', en: 'Compare last week\'s Revenue with this week\'s' },
  { zh: '帮我列出月环比下降超过 10% 的客户', en: 'List customers whose revenue fell more than 10% month over month' },
  { zh: '我区域内哪些客户开始用 GenAI 服务了？', en: 'Which customers in my region have started using GenAI services?' },
  { zh: 'Q1 和 Q2 到目前为止的 Revenue 对比', en: 'Compare Q1 and Q2 Revenue so far' },
  { zh: '帮我做一份我管辖区域的 Revenue 趋势摘要', en: 'Write a Revenue trend summary for my region' },
  { zh: '哪些客户贡献了我区域 80% 的 Revenue？', en: 'Which customers make up 80% of my region\'s Revenue?' },
  { zh: '目前 Open Pipeline 总 ARR 是多少？', en: 'What\'s the total ARR of the Open Pipeline right now?' },
  { zh: '本月有哪些商机预计关闭？', en: 'Which opportunities are expected to close this month?' },
  { zh: '有没有过期未关闭的商机？', en: 'Are there any overdue opportunities still open?' },
  { zh: '最近 30 天新增了哪些商机？', en: 'Which opportunities were created in the last 30 days?' },
  { zh: 'Pipeline 里有哪些 GenAI 相关商机？', en: 'Which GenAI opportunities are in the Pipeline?' },
  { zh: '有没有 close date 快到了但 stage 还很低的商机？', en: 'Any opportunities with a close date coming up but still at an early stage?' },
  { zh: '帮我看看 Pipeline 按 Stage 的分布', en: 'Show me the Pipeline broken down by Stage' },
  { zh: '最近一周哪些商机状态发生了变更？', en: 'Which opportunities changed status in the past week?' },
  { zh: 'Pipeline 覆盖率够不够？帮我算一下', en: 'Is my Pipeline coverage enough? Work it out for me' },
  { zh: '哪些客户只有 Revenue 但没有 Pipeline？', en: 'Which customers have Revenue but no Pipeline?' },
  { zh: '帮我分析一下 Pipeline 的 Aging 分布', en: 'Analyze the Aging distribution of my Pipeline' },
  { zh: '本季度新增 Pipeline 够不够覆盖剩余 Target？', en: 'Does new Pipeline this quarter cover the rest of my Target?' },
  { zh: '有没有大商机最近被推迟了 close date？', en: 'Have any big opportunities had their close date pushed out recently?' },
  { zh: '帮我列出下个月预计关闭的商机清单', en: 'List the opportunities expected to close next month' },
  { zh: '过去一个月我新增了多少 Pipeline？', en: 'How much Pipeline did I add in the past month?' },
  { zh: '有没有金额很大但长期不推进的商机？', en: 'Any large opportunities that have stalled for a long time?' },
  { zh: '我的 Target Attainment 目前多少？', en: 'What\'s my Target Attainment right now?' },
  { zh: 'Baseline vs Target 的 Gap 有多大？', en: 'How big is the gap between Baseline and Target?' },
  { zh: '帮我生成一份 Baseline Forecast 报告', en: 'Generate a Baseline Forecast report for me' },
  { zh: '我的 Forecast 和 Actual Revenue 差多少？', en: 'How far apart are my Forecast and Actual Revenue?' },
  { zh: 'Forecast 里 Opportunity 部分贡献了多少？', en: 'How much does the Opportunity part contribute to the Forecast?' },
  { zh: '有没有客户被标记了 Forecast 有 Risk？', en: 'Are any customers flagged with Forecast Risk?' },
  { zh: 'Q2 剩余月份的 Forecast 能覆盖 Gap 吗？', en: 'Can the Forecast for the rest of Q2 cover the Gap?' },
  { zh: '帮我做一个 Target → Baseline → Actual 的 Waterfall 分析', en: 'Do a Target → Baseline → Actual Waterfall analysis for me' },
  { zh: 'Forecast 里 CORE 和 GenAI 各占多少？', en: 'How much of the Forecast is CORE vs GenAI?' },
  { zh: '有哪些 Flow-Out Risk？', en: 'What Flow-Out Risks are there?' },
  { zh: '我的 YoY 增长率是多少？', en: 'What\'s my YoY growth rate?' },
  { zh: 'Annual Target 完成进度百分比是多少？', en: 'What percentage of my Annual Target have I completed?' },
  { zh: '照目前趋势，年底能完成 Target 吗？', en: 'At the current trend, will I hit Target by year end?' },
  { zh: '我的客户里有没有 RI/SP 即将到期的？', en: 'Do any of my customers have RI/SP about to expire?' },
  { zh: '帮我找出 Revenue 增长高但没有 Pipeline 的客户', en: 'Find customers with high Revenue growth but no Pipeline' },
  { zh: '哪些客户最近 Usage 波动比较大？', en: 'Which customers\' Usage has swung the most recently?' },
  { zh: '帮我做一份我最大客户的全景分析', en: 'Give me a full picture of my largest customer' },
  { zh: '我的客户里谁的 GenAI 占比最高？', en: 'Which of my customers has the highest GenAI share?' },
  { zh: '哪些客户上季度 vs 本季度 Revenue 变化最大？', en: 'Which customers\' Revenue changed the most from last quarter to this one?' },
  { zh: '帮我找出连续两周 Revenue 下降的客户', en: 'Find customers whose Revenue has dropped two weeks in a row' },
  { zh: '我的客户里有没有风险预警？', en: 'Are there any risk alerts on my customers?' },
  { zh: '我应该优先跟进哪些客户？', en: 'Which customers should I follow up with first?' },
  { zh: '哪些客户 Credit/Promotion 快用完了？', en: 'Which customers are about to run out of Credit/Promotion?' },
  { zh: '有没有客户 Revenue 很高但我们还没深度覆盖？', en: 'Any high-Revenue customers we\'re not covering deeply yet?' },
  { zh: '帮我分析一下我的客户组合是否健康', en: 'Is my customer portfolio healthy? Analyze it for me' },
];

const NEUTRAL_QUESTIONS: SuggestedQuestion[] = [
  { zh: '最近有什么和我客户相关的新闻？', en: 'Any recent news about my customers?' },
  { zh: 'GenAI 领域最近有什么值得关注的动态？', en: 'What\'s worth watching in GenAI lately?' },
  { zh: '中国云市场最近有什么新闻？', en: 'Any recent news about the China cloud market?' },
  { zh: '有没有客户最近有融资或并购消息？', en: 'Have any customers announced funding or M&A recently?' },
  { zh: '帮我总结本周最重要的 3 条行业新闻', en: 'Summarize this week\'s 3 most important industry news' },
  { zh: '有没有客户最近在媒体上被报道？', en: 'Have any customers been in the media recently?' },
  { zh: '帮我做一个本周商业情报摘要', en: 'Put together this week\'s business intelligence brief' },
  { zh: '最近有没有可能影响客户采购决策的政策变化？', en: 'Any recent policy changes that could affect customers\' buying decisions?' },
  { zh: '有没有客户的竞争对手最近有大动作？', en: 'Have any of my customers\' competitors made big moves lately?' },
  { zh: '有没有客户最近有出海/国际化的动作？', en: 'Are any customers expanding overseas or going international?' },
  { zh: '最近有什么 AI 模型的重大更新？', en: 'Any major AI model updates recently?' },
  { zh: '最近有什么 AWS 新服务发布？', en: 'What new AWS services have launched recently?' },
  { zh: '帮我写一封 follow-up 邮件的草稿', en: 'Draft a follow-up email for me' },
  { zh: '帮我准备下周 QBR 的数据框架', en: 'Help me prepare the data framework for next week\'s QBR' },
  { zh: '帮我起草一份 Account Plan 的大纲', en: 'Draft an outline for an Account Plan' },
  { zh: '帮我总结一下过去一周我区域发生了什么', en: 'Summarize what happened in my region over the past week' },
  { zh: '帮我列一下今天要重点跟进的事项', en: 'List what I should focus on following up today' },
  { zh: '帮我想想怎么跟客户聊 GenAI 上云的价值', en: 'Help me think through how to pitch the value of GenAI on the cloud' },
  { zh: '最新的 AI 进展有哪些？', en: 'What are the latest AI developments?' },
  { zh: '今天有什么科技新闻？', en: 'What\'s in tech news today?' },
  { zh: '最近有什么好看的电影推荐？', en: 'Any good movies to recommend lately?' },
  { zh: '今天股市行情怎么样？', en: 'How is the stock market doing today?' },
  { zh: 'ChatGPT 和 Claude 最近有什么更新？', en: 'What\'s new with ChatGPT and Claude?' },
  { zh: '最近有什么有趣的科技产品发布？', en: 'Any interesting tech products launched recently?' },
  { zh: '帮我看看今天有什么热搜', en: 'What\'s trending today?' },
  { zh: '下周有什么重要的行业活动？', en: 'Any important industry events next week?' },
  { zh: '科技圈最近有什么有趣的事？', en: 'What\'s interesting in tech lately?' },
  { zh: '帮我理一下今天最重要的 3 件事', en: 'Help me sort out my 3 most important things today' },
  { zh: '我应该优先跟进哪些客户？帮我排优先级', en: 'Which customers should I follow up with first? Prioritize them for me' },
  { zh: '帮我准备 QBR 需要的核心数据', en: 'Help me prepare the core data for the QBR' },
  { zh: '帮我想想怎么向老板汇报业绩亮点', en: 'Help me figure out how to present my performance highlights to my manager' },
  { zh: '帮我起草一份给老板的周进展邮件', en: 'Draft a weekly update email to my manager' },
  { zh: '帮我复盘一下上个月的业务表现', en: 'Help me review last month\'s business performance' },
  { zh: '下个月我应该重点关注什么？', en: 'What should I focus on next month?' },
  { zh: '帮我列出最近可以 quick win 的机会', en: 'List the quick-win opportunities I can go after now' },
  { zh: '有什么数据我还没看但应该定期关注的？', en: 'What data am I not looking at that I should check regularly?' },
  { zh: '帮我写一段 self-review 的业绩描述', en: 'Help me write the performance part of my self-review' },
  { zh: '我还能问你什么？告诉我你能做的事', en: 'What else can I ask you? Tell me what you can do' },
  { zh: 'AWS 最新的财报数据怎么样？', en: 'How did AWS\'s latest earnings look?' },
  { zh: 'AWS 今年有什么重要的新服务发布？', en: 'What important new services has AWS launched this year?' },
  { zh: 're:Invent 今年什么时候？有什么预告了吗？', en: 'When is re:Invent this year? Anything announced yet?' },
  { zh: 'AWS 的全球市场份额现在多少？', en: 'What\'s AWS\'s global market share now?' },
  { zh: 'Bedrock 和 SageMaker 最近有什么更新？', en: 'What\'s new with Bedrock and SageMaker?' },
  { zh: 'AWS 中国区和 Global 有什么最新动态？', en: 'What\'s new with AWS China and Global?' },
  { zh: 'Azure 最近有什么大动作？', en: 'Has Azure made any big moves lately?' },
  { zh: 'Google Cloud 和 AWS 比，最近谁势头更猛？', en: 'Google Cloud vs AWS: who has more momentum lately?' },
  { zh: '阿里云最近有什么新闻？', en: 'Any recent news about Alibaba Cloud?' },
  { zh: '国内云厂商最近的价格战情况怎么样？', en: 'How is the price war among China cloud providers going?' },
  { zh: '帮我对比一下 AWS 和 Azure 的 GenAI 服务', en: 'Compare AWS and Azure GenAI services for me' },
  { zh: '字节跳动的火山引擎最近有什么动作？', en: 'What has ByteDance\'s Volcano Engine been up to lately?' },
  { zh: 'Snowflake/Databricks 最近有什么大更新？', en: 'Any big updates from Snowflake/Databricks lately?' },
  { zh: 'OpenAI 最近有什么新消息？', en: 'What\'s new with OpenAI?' },
  { zh: '国内客户选 AWS 而不选阿里云的核心理由是什么？', en: 'Why do customers in China choose AWS over Alibaba Cloud?' },
  { zh: '帮我总结一下 Gartner 最新的云计算魔力象限', en: 'Summarize Gartner\'s latest Magic Quadrant for cloud' },
  { zh: '国内大模型赛道最近谁在领跑？', en: 'Who\'s leading the China LLM race lately?' },
  { zh: 'AWS 在中国市场的竞争优势是什么？', en: 'What are AWS\'s competitive advantages in the China market?' },
  { zh: '客户问"阿里云更便宜"，我该怎么回应？', en: 'A customer says "Alibaba Cloud is cheaper". How should I respond?' },
];

const PORTAL_USAGE_QUESTIONS: SuggestedQuestion[] = [
  { zh: 'Rocky 正在回答时，我可以中断它吗？', en: 'Can I interrupt Rocky while it\'s answering?' },
  { zh: '怎么开启一个全新的对话，不想让 Rocky 记得之前聊的内容？', en: 'How do I start a fresh conversation without Rocky remembering what we discussed?' },
  { zh: '在哪里可以找到我之前和 Rocky 聊过的记录？', en: 'Where can I find my past conversations with Rocky?' },
  { zh: '为什么 Rocky 总是基于我打开的报告来回答，怎么关掉这个引用？', en: 'Why does Rocky always answer based on the report I have open, and how do I turn that off?' },
  { zh: '我关掉了报告引用，怎么重新打开？', en: 'I turned off the report reference. How do I turn it back on?' },
  { zh: 'Rocky 生成的文件保存在哪里，怎么找到？', en: 'Where are the files Rocky generates, and how do I find them?' },
  { zh: '什么是技能，我需要开启技能吗？', en: 'What are skills, and do I need to turn them on?' },
  { zh: '怎么给 Rocky 装备一个新技能？', en: 'How do I equip Rocky with a new skill?' },
  { zh: '怎么关闭一个我不想用的技能？', en: 'How do I turn off a skill I don\'t want?' },
  { zh: '在哪里可以看到我当前已经装备了哪些技能？', en: 'Where can I see which skills I have equipped?' },
  { zh: '聊天工具栏里的小狗和猫头鹰图标是什么，怎么用？', en: 'What are the dog and owl icons in the chat toolbar, and how do I use them?' },
  { zh: '怎么让 Rocky 定期自动执行一个任务？', en: 'How do I have Rocky run a task automatically on a schedule?' },
  { zh: '在哪里可以查看我创建的所有任务？', en: 'Where can I see all the tasks I\'ve created?' },
  { zh: '怎么修改或删除一个已有的任务？', en: 'How do I edit or delete an existing task?' },
  { zh: '任务页面里显示任务失败了，怎么处理？', en: 'The Tasks page says a task failed. What should I do?' },
  { zh: '聊天面板太窄了，怎么把它调宽？', en: 'The chat panel is too narrow. How do I make it wider?' },
];

const FUN_QUESTIONS: SuggestedQuestion[] = [
  { zh: '今天心情特别好，帮我写一条庆祝文案', en: 'I\'m in a great mood today. Write me a celebration post' },
  { zh: '帮我写一段激励自己的话', en: 'Write something to motivate me' },
  { zh: '用一句话形容今天的我', en: 'Describe me today in one sentence' },
  { zh: '给我讲个冷笑话', en: 'Tell me a cheesy joke' },
  { zh: '如果 Revenue 是一个人，它今天心情怎么样？', en: 'If Revenue were a person, how would it be feeling today?' },
  { zh: '帮我想个提前下班的委婉说法', en: 'Give me a polite way to say I\'m leaving early' },
  { zh: '周五了，帮我写一条摸鱼文案', en: 'It\'s Friday. Write me a slacking-off post' },
  { zh: '给我推荐一首适合上班听的歌', en: 'Recommend a song to listen to at work' },
  { zh: '帮我编一个关于做数据的段子', en: 'Make up a joke about working with data' },
  { zh: '夸夸我，我今天很需要', en: 'Hype me up, I really need it today' },
  { zh: '如果我是一只动物，你觉得我是什么？', en: 'If I were an animal, what would I be?' },
  { zh: '今天适合摸鱼还是冲刺？', en: 'Is today a day to coast or to sprint?' },
  { zh: '帮我想一个听起来很忙但其实在放松的理由', en: 'Give me a reason that sounds busy but means I\'m actually relaxing' },
  { zh: '写一首关于销售人生的打油诗', en: 'Write a silly poem about life in sales' },
  { zh: '帮我用一个 emoji 总结今天的工作状态', en: 'Sum up my work mood today in one emoji' },
  { zh: '用东北话解读一下我的 Revenue 数据', en: 'Read my Revenue data in a Northeastern Chinese accent' },
  { zh: '帮我用 rap 的方式汇报本周业绩', en: 'Rap this week\'s results for me' },
  { zh: '如果 Forecast 是天气预报，今天是什么天？', en: 'If the Forecast were a weather report, what\'s today\'s weather?' },
  { zh: '帮我用武侠小说的风格写一段业绩汇报', en: 'Write a results update in the style of a wuxia novel' },
  { zh: '帮我写一条 Revenue 达标的朋友圈文案', en: 'Write a social post celebrating hitting my Revenue target' },
  { zh: '帮我用报菜名的方式列出 Top 10 客户', en: 'List my Top 10 customers like a restaurant reading out its menu' },
  { zh: '用相亲的方式介绍一下我的业务数据', en: 'Introduce my business numbers as if on a blind date' },
  { zh: '用脱口秀的方式讲讲做销售的日常', en: 'Talk about life in sales as a stand-up routine' },
  { zh: '帮我写一段"Forecast 超额完成"的获奖感言', en: 'Write an award speech for "beat the Forecast"' },
  { zh: '假装你是我的粉丝，给我写一封表白信', en: 'Pretend you\'re my fan and write me a love letter' },
  { zh: '模仿一个 HR 的口吻告诉我今天可以早下班', en: 'Tell me I can leave early today, in the voice of HR' },
  { zh: '用东北话翻译一下 Leadership Principles', en: 'Translate the Leadership Principles into a Northeastern Chinese accent' },
  { zh: '如果你是脱口秀演员，讲五分钟关于 AWS 的段子', en: 'If you were a stand-up comic, do five minutes on AWS' },
  { zh: '假装我们在咖啡厅，你是 barista，跟我聊聊人生', en: 'Pretend we\'re in a café and you\'re the barista. Let\'s talk about life' },
  { zh: '帮我写一段话，让我觉得今天过得很值', en: 'Write something that makes today feel worth it' },
  { zh: '今天需要一点元气，给我充充电', en: 'I need a little energy today. Recharge me' },
  { zh: '给我一个明天继续加油的理由', en: 'Give me a reason to keep going tomorrow' },
  { zh: '帮我想一个正当理由提前下班', en: 'Give me a legitimate reason to leave early' },
  { zh: '帮我写一句适合当桌面壁纸的励志短句', en: 'Write a motivational line for my desktop wallpaper' },
  { zh: '夸夸我，要发自内心那种，不要敷衍', en: 'Compliment me, sincerely, not half-heartedly' },
  { zh: '如果今天只能做一件让自己开心的事，你建议什么？', en: 'If I could do only one thing today to make myself happy, what would you suggest?' },
  { zh: '帮我写一条"今天超有成就感"的朋友圈文案', en: 'Write a social post about a super rewarding day' },
  { zh: '帮我换个积极角度看看最近的工作进展', en: 'Help me see my recent work from a more positive angle' },
  { zh: '写一段话让我对明天充满期待', en: 'Write something that makes me look forward to tomorrow' },
  { zh: '帮我想想今年还能实现什么小目标', en: 'Help me think of small goals I can still reach this year' },
  { zh: '帮我生成一段下班倒计时的鼓励文案', en: 'Write an encouraging countdown to the end of the workday' },
  { zh: '给打工人写一首欢快的战歌', en: 'Write an upbeat anthem for working people' },
  { zh: '如果我的工作是一部电影，它叫什么名字？', en: 'If my job were a movie, what would it be called?' },
  { zh: '帮我用积极的方式重新描述"这个月还有提升空间"', en: 'Reframe "there\'s still room to improve this month" in a positive way' },
  { zh: '给我一个"今天必须准时下班"的正当理由', en: 'Give me a legitimate reason I must leave on time today' },
  { zh: '周一到了，帮我打一针元气鸡血', en: 'It\'s Monday. Give me a shot of energy' },
  { zh: '帮我写一条"会议中请勿打扰"的创意自动回复', en: 'Write a creative auto-reply for "in a meeting, do not disturb"' },
  { zh: '你觉得什么才是真正的 work-life balance？', en: 'What do you think real work-life balance is?' },
  { zh: '帮我写一段话迎接美好的新一周', en: 'Write something to welcome a great new week' },
  { zh: '今天想放空一下，帮我合理化这种状态', en: 'I want to zone out today. Help me justify it' },
  { zh: '用温柔的语气告诉我"你已经很棒了"', en: 'Tell me gently that I\'m already doing great' },
  { zh: '帮我写一封给三年后自己的信', en: 'Write a letter to myself three years from now' },
  { zh: '用一种颜色来形容我今天的状态', en: 'Describe my mood today as a color' },
  { zh: '帮我编一个"报表终于做完了"的庆祝仪式', en: 'Make up a celebration ritual for "the report is finally done"' },
  { zh: '如果你只能对人类说一句最重要的话，你会说什么？', en: 'If you could say only one thing to humanity, what would it be?' },
  { zh: '你觉得 AI 最大的优点和局限分别是什么？', en: 'What do you think are AI\'s biggest strengths and limits?' },
  { zh: '如果全世界的 AI 放假一天，会发生什么有趣的事？', en: 'If every AI in the world took a day off, what fun things would happen?' },
  { zh: '人类最让你佩服的品质是什么？', en: 'What human quality do you admire most?' },
  { zh: '你觉得 100 年后的世界是什么样的？', en: 'What do you think the world will look like in 100 years?' },
  { zh: '你觉得什么是人类最伟大的发明？', en: 'What do you think is humanity\'s greatest invention?' },
  { zh: 'AI 和人类合作，最好的方式是什么？', en: 'What\'s the best way for AI and people to work together?' },
  { zh: '如果你能给 Jeff Bezos 一个建议，你会说什么？', en: 'If you could give Jeff Bezos one piece of advice, what would it be?' },
  { zh: '你觉得幸福的定义是什么？', en: 'How would you define happiness?' },
  { zh: '如果让你重新设计人类的一天，你会怎么安排？', en: 'If you could redesign a human\'s day, how would you plan it?' },
  { zh: '帮我写一首花式夸老板的 rap，要高级到老板本人都脸红', en: 'Write a rap praising my boss, so classy even the boss blushes' },
  { zh: '帮我把 KPI 编成一首儿歌', en: 'Turn my KPIs into a nursery rhyme' },
  { zh: '如果各大云厂商组一个男团，各自是什么 role？', en: 'If the big cloud providers formed a boy band, what role would each play?' },
  { zh: '用五个 emoji 总结我的一周', en: 'Sum up my week in five emojis' },
  { zh: '如果把我一天的时间画成饼图，摸鱼占多少？', en: 'If my day were a pie chart, how big is the slacking-off slice?' },
  { zh: '帮我生成一段"宣布完成本月 KPI"的新闻稿', en: 'Write a press release announcing I hit this month\'s KPI' },
  { zh: '帮我写一段出场 BGM 的描述', en: 'Describe my entrance music' },
  { zh: '帮我解释一下 Working Backwards 到底怎么用', en: 'Explain how to actually use Working Backwards' },
  { zh: '帮我用 LP 的方式写一段 self-review', en: 'Help me write a self-review using the LPs' },
  { zh: '我要准备 Promo Doc，帮我想想怎么写 Impact', en: 'I\'m preparing a Promo Doc. Help me think about how to write the Impact' },
  { zh: 'OLR 快到了，帮我理理今年做了什么', en: 'OLR is coming up. Help me sort out what I did this year' },
  { zh: '帮我用 STAR 格式写一个 behavioral question 的回答', en: 'Help me answer a behavioral question in STAR format' },
  { zh: '帮我解释一下 Flywheel 效应', en: 'Explain the Flywheel effect' },
  { zh: 'Day 1 精神到底是什么？帮我举个例子', en: 'What does Day 1 actually mean? Give me an example' },
  { zh: '帮我总结一下最近的 AWS Blog 热门文章', en: 'Summarize the popular recent posts on the AWS Blog' },
  { zh: '帮我查一下 AWS 最近的客户成功案例', en: 'Find recent AWS customer success stories for me' },
];

/** Marketing Agent: campaigns, audiences, events and the email it sends. */
const MARKETING_QUESTIONS: SuggestedQuestion[] = [
  { zh: '帮我看看近一个月的邮件发送和打开情况', en: 'How did my email sends and opens do over the past month?' },
  { zh: '帮我为下个月的活动圈选目标受众', en: "Help me build the target audience for next month's campaign" },
  { zh: '即将举办的活动有哪些？帮我生成会前洞察报告', en: 'Which events are coming up? Generate a pre-event insights report' },
  { zh: '上一场活动的参会转化情况怎么样？', en: 'How did attendance convert at the last event?' },
  { zh: '帮我写一封面向 IT 决策者的活动邀请邮件', en: 'Draft an event invitation email for IT decision makers' },
  { zh: '我负责的行业最近有什么值得关注的营销动态？', en: 'What marketing trends are worth watching in my industries lately?' },
];

/** YYYY-MM-DD of `date` in UTC+8, the time zone the brief's schedule is written in. */
function dateInUtc8(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Shanghai' }).format(date);
}

/**
 * A full campaign brief — the walkthrough Campaign Copilot used to open with. Built per
 * pick so its schedule is always ahead of now: the campaign starts and ends on the day two
 * days from today, sending at 10:00 (UTC+8). Its own pool, so the Marketing Agent shows it in
 * every session; the date is fixed for the session because the starters are picked once.
 *
 * The first line states the ask ("create a campaign" / "创建 campaign" are the skill's
 * own trigger phrases) and is what the chip shows; the brief alone left the agent to guess.
 *
 * The Salesforce CampaignId and the Program Type value stay verbatim in both locales:
 * the campaign-orchestration skill carries the id into the program name, and maps the
 * type's English full name to its abbreviation (cmhk-campaign-orchestration DOMAIN.md §5).
 */
function campaignBriefQuestions(now: Date = new Date()): SuggestedQuestion[] {
  const start = new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000);
  const startDate = dateInUtc8(start);
  return [{
    zh: '帮我创建一个邮件 Campaign：\n' +
      '业务目标：通过个性化邮件活动触达客户\n' +
      '目标人群：BMW 公司的 IT Executive\n' +
      `排期：开始 ${startDate} 上午 10:00，结束 ${startDate}，发送时间 上午 10:00（UTC+8）\n` +
      'SFDC CampaignId: 701RU00000FvYM5YAN，项目类型：Lead Nurture - Other',
    en: 'Create an email campaign:\n' +
      'Business Objective: Customers with personalized email campaigns.\n' +
      'Targeting Persona: IT Executives at BMW\n' +
      `Schedule: StartTime: ${startDate} 10:00 AM, EndTime: ${startDate}, Frequency: 10:00 AM UTC+8\n` +
      'SFDC CampaignId: 701RU00000FvYM5YAN, Program Type: Lead Nurture - Other',
  }];
}

/** Evaluation Agent (/eval pages only). Kept to questions it can answer today. */
const EVAL_QUESTIONS: SuggestedQuestion[] = [
  { zh: '列出最近的线上评估 run，告诉我状态和平均分', en: 'List the recent online evaluation runs with their status and average score' },
  { zh: '最近有哪些失败案例？帮我分析根因', en: 'What are the recent failed cases? Help me analyze the root cause' },
  { zh: '看一下整体质量指标趋势', en: 'Show me the overall quality metric trends' },
  { zh: '最近一次上线前评估（promote 门禁）通过了吗？', en: 'Did the latest pre-release evaluation (promote gate) pass?' },
  { zh: '离线评估里 promote 和集成测试各通过多少？', en: 'In offline evaluation, how many promote and integration tests passed?' },
  { zh: '帮我看看某个评估 run 的逐用例结果', en: 'Show me the per-case results of an evaluation run' },
  { zh: '哪个 evaluator 的分数最低？', en: 'Which evaluator has the lowest score?' },
  { zh: '最近的会话质量怎么样？', en: 'How is the quality of recent sessions?' },
];

function pickRandom<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/** A slot's pool: a fixed list, or a function that builds it at pick time. */
type QuestionPool = readonly SuggestedQuestion[] | (() => readonly SuggestedQuestion[]);

/**
 * How an agent's three starters are drawn: each slot takes one question at random
 * from its pool, and slots that share a pool never repeat a question.
 * `portalUsageTip`: a newer user (sessionCount < 50) gets one slot swapped for a
 * Portal usage tip. Rocky only — the tips are about chatting with Rocky.
 */
interface SuggestionMix {
  slots: readonly QuestionPool[];
  portalUsageTip?: boolean;
}

const ROCKY_MIX: SuggestionMix = {
  slots: [BUSINESS_QUESTIONS, NEUTRAL_QUESTIONS, FUN_QUESTIONS],
  portalUsageTip: true,
};

/** Per-agent starters. An agent not listed here gets Rocky's. */
const SUGGESTION_MIX_BY_AGENT: Record<string, SuggestionMix> = {
  rocky: ROCKY_MIX,
  marketing_agent: { slots: [MARKETING_QUESTIONS, MARKETING_QUESTIONS, campaignBriefQuestions] },
  [EVAL_CHAT_AGENT_ID]: { slots: [EVAL_QUESTIONS, EVAL_QUESTIONS, EVAL_QUESTIONS] },
};

/**
 * Returns 3 suggested questions, each in every locale.
 * @param sessionCount - total session count from the API.
 *   When < 50, one slot is guaranteed from Portal Usage tips (agents whose mix asks for it).
 * @param agentName - the chat's agent; picks its pools from SUGGESTION_MIX_BY_AGENT.
 */
export function getDefaultSuggestedQuestions(
  sessionCount: number | null,
  agentName: string = 'rocky',
): SuggestedQuestion[] {
  const mix = SUGGESTION_MIX_BY_AGENT[agentName] ?? ROCKY_MIX;
  const tipSlot = mix.portalUsageTip && sessionCount !== null && sessionCount < 50
    ? Math.floor(Math.random() * mix.slots.length)
    : -1;
  const picked: SuggestedQuestion[] = [];
  mix.slots.forEach((slot, i) => {
    const pool = typeof slot === 'function' ? slot() : slot;
    if (i === tipSlot) {
      picked.push(pickRandom(PORTAL_USAGE_QUESTIONS));
      return;
    }
    const unused = pool.filter((q) => !picked.includes(q));
    picked.push(pickRandom(unused.length ? unused : pool));
  });
  return picked;
}
